import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { InferType, object, string } from 'yup';
import { toast } from 'react-toastify';
import Button from '../../../components/common/Button';
import { Input, TextArea } from '../../../components/common/Form';
import RestaurantWizardHeader from '../RestaurantWizard/RestaurantWizardHeader';
import { useBrandContext } from '../../../contexts/BrandContext/BrandContext';
import { createBrandFaq, getBrandFaqs, updateBrandFaq } from '../../../api/brandFaqs';
import { PAGE_BRAND_FAQS } from '../../../constants/UriConstants';
import './styles/_brand-faqs.scss';

const FaqSchema = object({
  question: string().default('').trim().required('Enter the question.'),
  answer: string().default('').trim().required('Enter the answer.')
});

type FaqValues = InferType<typeof FaqSchema>;

const EMPTY_FAQ: FaqValues = { question: '', answer: '' };

const BrandFaqFormPage = () => {
  const navigate = useNavigate();
  const { faqId } = useParams<{ faqId: string }>();
  const { currentBrand } = useBrandContext();
  const brandID = currentBrand?.id;
  const editingID = faqId && faqId !== 'new' ? Number(faqId) : null;

  const [initialValues, setInitialValues] = useState<FaqValues>(EMPTY_FAQ);
  const [isSaving, setIsSaving] = useState(false);
  const addAnother = useRef(false);
  const listPath = `/${brandID}/${PAGE_BRAND_FAQS}`;

  useEffect(() => {
    if (!brandID || editingID === null) return undefined;
    let canceled = false;
    getBrandFaqs(brandID)
      .then((faqs) => {
        if (canceled) return;
        const faq = faqs.find((candidate) => candidate.id === editingID);
        if (faq) {
          setInitialValues({ question: faq.question, answer: faq.answer });
        } else {
          toast.error('That FAQ no longer exists.', { position: toast.POSITION.TOP_RIGHT });
          navigate(listPath);
        }
      })
      .catch(() => {
        if (!canceled) toast.error('Could not load the FAQ. Please try again.', { position: toast.POSITION.TOP_RIGHT });
      });
    return () => {
      canceled = true;
    };
  }, [brandID, editingID, listPath, navigate]);

  const formik = useFormik<FaqValues>({
    initialValues,
    validationSchema: FaqSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      if (!brandID) return;
      const faq = { question: values.question.trim(), answer: values.answer.trim() };
      setIsSaving(true);
      try {
        if (editingID === null) {
          await createBrandFaq(brandID, faq);
        } else {
          await updateBrandFaq(brandID, editingID, faq);
        }
        toast.success('Successfully Saved!', { position: toast.POSITION.TOP_RIGHT });
        if (addAnother.current) {
          resetForm({ values: EMPTY_FAQ });
        } else {
          navigate(listPath);
        }
      } catch {
        toast.error('Could not save the FAQ. Please try again.', { position: toast.POSITION.TOP_RIGHT });
      } finally {
        setIsSaving(false);
      }
    }
  });

  const submit = (andAddAnother: boolean) => {
    addAnother.current = andAddAnother;
    formik.handleSubmit();
  };

  return (
    <div className="restaurant-wizard-container brand-faq-form-container">
      <div className="restaurant-wizard-hero-background" aria-hidden="true" />
      <div className="restaurant-wizard-scroll-body">
        <RestaurantWizardHeader title="FREQUENTLY ASKED QUESTIONS" />
        <form
          className="restaurant-wizard-content brand-faq-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit(false);
          }}
        >
          <Input
            name="question"
            label="FREQUENTLY ASKED QUESTION"
            className="brand-faq-form__field"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.question}
            touched={formik.touched.question}
            error={formik.errors.question}
          />
          <TextArea
            name="answer"
            label="ANSWER"
            className="brand-faq-form__field brand-faq-form__answer"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.answer}
            touched={!!formik.touched.answer}
            error={formik.errors.answer ?? ''}
          />
          <div className="brand-faq-form__actions">
            <Button
              className="brand-faq-form__button brand-faq-form__button--cancel"
              onClick={() => navigate(listPath)}
            >
              CANCEL
            </Button>
            {editingID === null && (
              <Button
                className="brand-faq-form__button brand-faq-form__button--add-another"
                onClick={() => submit(true)}
                isDisabled={isSaving}
              >
                SAVE &amp; ADD QUESTION
              </Button>
            )}
            <Button className="brand-faq-form__button brand-faq-form__button--save" submit isDisabled={isSaving}>
              SAVE
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrandFaqFormPage;
